import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { TriangleLogo } from "@/components/TriangleLogo";
import { LegalLinks } from "@/components/shared/LegalLinks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { supabase } from "@/lib/supabase";
import { getLegalDocumentHashes } from "@/features/consent/legalDocuments";
import {
  submitBetaSignup,
  isBetaFormEnabled,
} from "@/features/admin/api/betaSignups";

type Status = "idle" | "submitting" | "success" | "error";

const ERROR_MESSAGES: Record<string, string> = {
  email_invalid: "Email inválido. Verifique o endereço informado.",
  name_required: "Informe seu nome completo.",
  commitment_required: "Você precisa confirmar o compromisso para participar.",
  privacy_notice_required: "Leia a Política de Privacidade e confirme sua ciência para continuar.",
  rate_limit_exceeded: "Muitas tentativas em pouco tempo. Aguarde alguns minutos antes de tentar novamente.",
  temporarily_unavailable: "As inscrições estão temporariamente indisponíveis. Tente novamente em alguns minutos.",
  legal_documents_changed: "A Política de Privacidade foi atualizada. Recarregue a página e confira o texto antes de continuar.",
  form_disabled: "As inscrições estão encerradas no momento. Acompanhe novidades nas nossas redes.",
};

export default function InscricaoBeta() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [committed, setCommitted] = useState(false);
  const [privacyNoticeAcknowledged, setPrivacyNoticeAcknowledged] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: formEnabled, isLoading: loadingToggle } = useQuery({
    queryKey: ["beta_form_enabled"],
    queryFn: () => isBetaFormEnabled(supabase),
    refetchOnWindowFocus: false,
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg(null);
    try {
      const hashes = await getLegalDocumentHashes();
      await submitBetaSignup(supabase, {
        full_name: fullName,
        email,
        committed,
        privacy_notice_acknowledged: privacyNoticeAcknowledged,
        privacy_notice_hash: hashes.privacy,
      });
      setStatus("success");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro inesperado.";
      const code = message.toLowerCase();
      const mapped = Object.keys(ERROR_MESSAGES).find((k) => code.includes(k));
      setErrorMsg(mapped ? ERROR_MESSAGES[mapped] : "Não foi possível enviar. Tente novamente.");
      setStatus("error");
    }
  }

  const disabled =
    status === "submitting" ||
    !fullName.trim() ||
    !email.trim() ||
    !committed ||
    !privacyNoticeAcknowledged;
  const formClosed = !loadingToggle && formEnabled === false;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-background">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <TriangleLogo size="lg" animate />
          <h1 className="mt-5 text-2xl font-semibold text-foreground text-center">
            Acesso <span className="accent-text">Beta</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground text-center max-w-xs">
            Inscreva-se para participar do beta gratuito. Vagas limitadas para quem se compromete a usar e dar feedback.
          </p>
        </div>

        {formClosed ? (
          <div className="rounded-xl border border-border/50 bg-card p-6 text-center space-y-3">
            <WarningCircle className="w-10 h-10 mx-auto text-warning" weight="duotone" />
            <p className="text-sm text-foreground font-medium">Inscrições encerradas</p>
            <p className="text-xs text-muted-foreground">
              No momento não estamos aceitando novas inscrições. Acompanhe nossas redes para o próximo lote.
            </p>
          </div>
        ) : status === "success" ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center space-y-4">
            <CheckCircle className="w-10 h-10 mx-auto text-emerald-400" weight="duotone" />
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Solicitação recebida</p>
              <p className="text-xs text-muted-foreground">
                Se houver uma próxima etapa, enviaremos uma atualização pelo contato informado.
              </p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="rounded-xl border border-border/50 bg-card p-6 space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="full-name">Nome completo</Label>
              <Input
                id="full-name"
                type="text"
                autoComplete="name"
                placeholder="Ex: Joana Silva"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={status === "submitting"}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={status === "submitting"}
              />
              <p className="text-[11px] text-muted-foreground">
                Use o mesmo email com que vai criar sua conta após aprovação.
              </p>
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="commitment"
                checked={committed}
                onCheckedChange={(v) => setCommitted(Boolean(v))}
                disabled={status === "submitting"}
                className="mt-0.5"
              />
              <Label
                htmlFor="commitment"
                className="text-xs font-normal text-muted-foreground leading-snug cursor-pointer"
              >
                Me comprometo a usar a ferramenta, reportar bugs e enviar feedback para ajudar a evoluir o produto durante o beta.
              </Label>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="beta-privacy-notice"
                checked={privacyNoticeAcknowledged}
                onCheckedChange={(v) => setPrivacyNoticeAcknowledged(v === true)}
                disabled={status === "submitting"}
                className="mt-0.5"
                aria-describedby="beta-privacy-notice-description"
              />
              <Label
                htmlFor="beta-privacy-notice"
                className="text-xs font-normal text-muted-foreground leading-snug cursor-pointer"
              >
                Li a{" "}
                <Link
                  to="/legal/privacidade"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Política de Privacidade
                </Link>
                {" "}e estou ciente de que meu nome e email serão usados para analisar
                minha inscrição no beta e comunicar o resultado.
              </Label>
              <span id="beta-privacy-notice-description" className="sr-only">
                Esta ciência não autoriza comunicações de marketing.
              </span>
            </div>

            {status === "error" && errorMsg ? (
              <p
                role="alert"
                className="text-xs text-destructive border border-destructive/30 bg-destructive/10 rounded px-3 py-2"
              >
                {errorMsg}
              </p>
            ) : null}

            <Button type="submit" className="w-full gap-2" disabled={disabled}>
              {status === "submitting" ? "Enviando..." : "Quero participar"}
            </Button>
          </form>
        )}

        <p className="text-[11px] text-center text-muted-foreground mt-6">
          Já tem conta?{" "}
          <a href="/login" className="text-primary hover:underline">
            Entrar
          </a>
        </p>
        <LegalLinks className="mt-4" openInNewTab />
      </div>
    </div>
  );
}
